/**
 * SOLID Review
 * S — Strong. One concern: the browser's window and document behind the consent page role.
 * O — Good. A new CMP touchpoint is one more method.
 * L — Strong. Implements `IServiceConsentPage` exactly.
 * I — Strong. Five operations.
 * D — Strong. The window is handed in, as a supplier; the document is the window's.
 */
import type { IServiceConsentPage, TcfApi } from './IServiceConsentPage';

declare global {
  interface Window {
    __tcfapi?: TcfApi;
    googlefc?: {
      callbackQueue?: Array<() => void>;
      showRevocationMessage?: () => void;
    };
    dataLayer?: unknown[];
  }
}

/**
 * THE CERTIFIED CMP'S PAGE, FROM THE BROWSER (frontend-infrastructure-oo P2, 2026-10-28, §28.287) — what `ConsentGateway` did to
 * the window and the document itself until P2, reached through the window the composition root hands it (the document is the
 * window's), so nothing here names a global.
 */
export class BrowserConsentPage implements IServiceConsentPage {
  constructor(private readonly window: () => Window) {}

  tcfApi(): TcfApi | undefined {
    return this.window().__tcfapi;
  }

  /**
   * Only "opened" if Funding Choices can present the revocation message right now. It once pushed the call onto `callbackQueue`
   * when `showRevocationMessage` wasn't ready, but Funding Choices never flushes that queue when there is no consent message for
   * the visitor's region (or before it finishes loading), so the "Cookie settings" button silently did nothing; reporting
   * failure lets the caller fall back to the first-party preferences modal.
   */
  showRevocationMessage(): boolean {
    const googlefc = this.window().googlefc;
    if (googlefc && typeof googlefc.showRevocationMessage === 'function') {
      googlefc.showRevocationMessage();
      return true;
    }
    return false;
  }

  dataLayer(): unknown[] {
    const window = this.window();
    return (window.dataLayer = window.dataLayer ?? []);
  }

  hasElement(id: string): boolean {
    return this.window().document.getElementById(id) !== null;
  }

  appendScript(id: string, src: string): void {
    const document = this.window().document;
    const script = document.createElement('script');
    script.id = id;
    script.async = true;
    script.src = src;
    (document.head ?? document.documentElement).appendChild(script);
  }
}
